import { useCallback, useEffect, useMemo, useRef } from 'react';
import { cn } from './utils.js';

// Faithful port of the iykyk-l4-firstmate tile mechanic (extraction §2), GSAP-free:
// retargeting exponential springs (≈ power3 / 0.5s settle), perspective(900px) in
// the element's own transform, cursor-tracking glare via --gx/--gy, and an
// entry-direction "raking glint" set once per hover session. Fine pointers only.
export function RockingPanel({ as: Component = 'article', className, children, ...props }) {
  const ref = useRef(null);
  const spring = useRef({ rx: 0, ry: 0, lift: 0, tx: 0, ty: 0, tl: 0, raf: 0, last: 0 });

  const enabled = useMemo(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(pointer: fine)').matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  const loop = useCallback((now) => {
    const st = spring.current;
    const node = ref.current;
    if (!node) {
      st.raf = 0;
      return;
    }
    const dt = Math.min(0.05, (now - st.last) / 1000);
    st.last = now;
    const k = 1 - Math.exp(-9 * dt);
    st.rx += (st.tx - st.rx) * k;
    st.ry += (st.ty - st.ry) * k;
    st.lift += (st.tl - st.lift) * k;
    node.style.transform = `perspective(900px) rotateX(${st.rx.toFixed(3)}deg) rotateY(${st.ry.toFixed(3)}deg) translateY(${st.lift.toFixed(2)}px)`;
    const settled =
      Math.abs(st.tx - st.rx) + Math.abs(st.ty - st.ry) + Math.abs(st.tl - st.lift) < 0.02;
    if (settled && st.tx === 0 && st.ty === 0 && st.tl === 0) {
      node.style.transform = '';
      st.rx = 0;
      st.ry = 0;
      st.lift = 0;
      st.raf = 0;
      return;
    }
    st.raf = requestAnimationFrame(loop);
  }, []);

  const kick = useCallback(() => {
    const st = spring.current;
    if (!st.raf) {
      st.last = performance.now();
      st.raf = requestAnimationFrame(loop);
    }
  }, [loop]);

  useEffect(() => {
    return () => {
      if (spring.current.raf) cancelAnimationFrame(spring.current.raf);
    };
  }, []);

  const handlePointerEnter = useCallback(
    (event) => {
      if (!enabled) return;
      const node = ref.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      node.dataset.glint = event.clientX - rect.left < rect.width / 2 ? 'l' : 'r';
      spring.current.tl = -4;
      kick();
    },
    [enabled, kick],
  );

  const handlePointerMove = useCallback(
    (event) => {
      if (!enabled) return;
      const node = ref.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const px = (event.clientX - rect.left) / rect.width;
      const py = (event.clientY - rect.top) / rect.height;
      spring.current.ty = (px - 0.5) * 9;
      spring.current.tx = (0.5 - py) * 8;
      node.style.setProperty('--gx', `${(px * 100).toFixed(2)}%`);
      node.style.setProperty('--gy', `${(py * 100).toFixed(2)}%`);
      kick();
    },
    [enabled, kick],
  );

  const handlePointerLeave = useCallback(() => {
    const st = spring.current;
    st.tx = 0;
    st.ty = 0;
    st.tl = 0;
    kick();
  }, [kick]);

  return (
    <Component
      ref={ref}
      className={cn('rocking-panel', className)}
      onPointerEnter={handlePointerEnter}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
      {...props}
    >
      {children}
      <span className="panel-glare" aria-hidden="true" />
    </Component>
  );
}
